/*
 * Strongly recommend reading documentation on Notion before trying to understand this file.
 * https://www.notion.so/Device-Version-Selectors-dbc6ee6b85c9436ca0581fb8cdb7829e
 */

import { DeviceVersion } from "../schemas/commonSubSchemas/deviceVersion.js";

export function deviceVersionObjectToString(deviceVersion: DeviceVersion) {
    return `${deviceVersion.generation}.${deviceVersion.productLine}.${deviceVersion.hardwareVersion}.${deviceVersion.hardwareVariant}.${deviceVersion.firmwareVersion}`;
}

export function deviceVersionStringToObject(deviceVersion: string): DeviceVersion {
    const segments = deviceVersion.split(".");
    return {
        generation: parseInt(segments[0]),
        productLine: segments[1],
        hardwareVersion: parseInt(segments[2]),
        hardwareVariant: segments[3],
        firmwareVersion: parseInt(segments[4]),
    } as DeviceVersion;
}

// specify what is allowable in a string-based condition like the product line or the hardware variant:
const stringBasicTermRegexPattern = /([a-z]+|\*)/;

// specify what is allowable in a numeric condition like the hardware version or firmware version:
const integerBasicTermRegexPattern = /([0-9]+|[0-9]+-|[0-9]+\+|[0-9]+:[0-9]+|\*)/;

/**
 * Returns true if the device version selector syntax is valid.
 *
 * Known issue: though "?" is a possible character in a version selector this function will consider a
 * string containing "?" as invalid and return false.
 *
 * @param deviceVersionSelector
 * @returns {boolean}
 */
export function isDeviceVersionSelectorValid(deviceVersionSelector: string) {
    // Use basic term regex patterns to build a regex pattern for all valid version selector strings

    // Define regex for basic or compound string terms
    const stringTermRegex = new RegExp(
        `(${stringBasicTermRegexPattern.source}|(\\[${stringBasicTermRegexPattern.source}(,${stringBasicTermRegexPattern.source})*\\]))`,
    );

    // Define regex for basic or compound integer terms
    const integerTermRegex = new RegExp(
        `(${integerBasicTermRegexPattern.source}|(\\[${integerBasicTermRegexPattern.source}(,${integerBasicTermRegexPattern.source})*\\]))`,
    );

    // Define regex for a basic version selector (integer.string.integer.string.integer)
    const basicSelectorRegex = new RegExp(
        `${integerTermRegex.source}\\.${stringTermRegex.source}\\.` +
            `${integerTermRegex.source}\\.${stringTermRegex.source}\\.` +
            `${integerTermRegex.source}`,
    );

    // Define regex for basic or compound version selectors (that match the whole string)
    const selectorRegex = new RegExp(`^((${basicSelectorRegex.source})|((!?\\(${basicSelectorRegex.source}\\))+))$`);

    // Whitespace is currently ignored in validating selectors
    return selectorRegex.test(deviceVersionSelector.replace(/ /g, ""));
}

// See notion docs for more information on the device version system:
// https://www.notion.so/Device-version-system-dbc6ee6b85c9436ca0581fb8cdb7829e
export function doesDeviceVersionMatchSelector(deviceVersion: DeviceVersion | string, selector: string): boolean {
    // Coerce the device version to an object
    let deviceVersionObject;
    if (typeof deviceVersion === "string") {
        deviceVersionObject = deviceVersionStringToObject(deviceVersion);
    } else {
        deviceVersionObject = deviceVersion;
    }

    selector = sanitizeVersionSelector(selector);

    // Throw an error if the selector is invalid
    if (!isDeviceVersionSelectorValid(selector)) {
        throw new Error(
            `Invalid device version selector: "${selector}" must be a valid version selector. See docs ` +
                `on Notion: https://www.notion.so/Device-Version-Selectors-dbc6ee6b85c9436ca0581fb8cdb7829e`,
        );
    }

    // Each ") (" separates individual constraints, with a leading "!" indicating a negative constraint
    // We need to break them apart, but maintain a & or ! at the
    // beginning of each constraint so we know if it's a positive or a negative constraint
    let doubledConstraints = selector.replace(/\) *\(/g, "&_&");

    doubledConstraints = doubledConstraints.replace(/\) *!\(/g, "!_!");

    doubledConstraints = doubledConstraints.replace(/\)/g, "");
    doubledConstraints = doubledConstraints.replace(/\(/g, "");
    const individualConstraints = doubledConstraints.split(/[!&]_/);

    // now that we have each single constraint on it's own, we go through them and see which ones
    // match our device version string
    let included = false;
    let excluded = false;
    individualConstraints.forEach((individualConstraint) => {
        individualConstraint = individualConstraint.trim();

        // If a constraint starts with a ! or a & it needs to be trimmed off before being passed to isVersionWithinSingleConstraint
        // we also need to make sure that the output of isVersionWithinSingleConstraint is handled correctly based on and ! or &
        if (individualConstraint[0] === "!") {
            excluded =
                excluded ||
                isVersionWithinSingleConstraint(deviceVersionObject, individualConstraint.substring(1).trim());
        } else if (individualConstraint[0] === "&") {
            included =
                included ||
                isVersionWithinSingleConstraint(deviceVersionObject, individualConstraint.substring(1).trim());
        } else {
            included = included || isVersionWithinSingleConstraint(deviceVersionObject, individualConstraint.trim());
        }
    });

    // if at least one positive constraint includes our device and no negative constraints include
    // our device, then this device is included.
    return included && !excluded;
}

/**
 * Tests if a device version satisfies a single constraint. This eliminates the complexity of chained
 * constraints with & and !
 * @param deviceVersion
 * @param constraint
 * @return {boolean}
 */
function isVersionWithinSingleConstraint(deviceVersion: DeviceVersion, constraint: string): boolean {
    const termValues = [
        deviceVersion.generation,
        deviceVersion.productLine?.toLowerCase(),
        deviceVersion.hardwareVersion,
        deviceVersion.hardwareVariant?.toLowerCase(),
        deviceVersion.firmwareVersion,
    ];
    const termConditions = constraint.split(".");

    // make sure the term conditions are set properly.
    if (termConditions.length !== termValues.length) {
        throw Error(
            `Version constraint ${constraint} is invalid because it has the wrong number of segments. Expected ${termValues.length} segments but got ${termConditions.length}`,
        );
    }

    return !termConditions.some((condition, i) => !doesTermSatisfySingleCondition(termValues[i], condition));
}

/**
 * Tests if a particular value satisfies a particular condition. So if "1" satisfies "1-5" or if micro satisfied "micro, mega"
 * @param termValue
 * @param condition
 * @return {boolean}
 */
function doesTermSatisfySingleCondition(termValue: number | string | undefined, condition: string): boolean {
    condition = condition.replace(/ /g, "");
    condition = condition.replace(/\[/g, "");
    condition = condition.replace(/]/g, "");

    // first, if there are commas, we need to break this up in to multiple checks.
    const options = condition.split(",");
    if (options.length > 1) {
        return options.some((option) => doesTermSatisfySingleCondition(termValue, option));
    }

    // valid conditions are:
    // "*" or "x-y" or "x+" or "x-" or "x" or "sometext" where x and y are numbers
    const validCondition = integerBasicTermRegexPattern.test(condition) || stringBasicTermRegexPattern.test(condition);

    if (!validCondition) {
        throw Error(`Condition ${condition} in version selector string is invalid`);
    }

    // deal with wildcard
    if (condition === "*") {
        return true;
    }
    condition = condition.trim();

    // deal with x+ and x-
    const lastChar = condition[condition.length - 1];
    if (lastChar === "+") {
        const minValue = parseInt(condition);
        return (termValue as number) >= minValue;
    }
    if (lastChar === "-") {
        const maxValue = parseInt(condition);
        return (termValue as number) <= maxValue;
    }
    // deal with x-y
    if (/[0-9]+:[0-9]+/.test(condition)) {
        const values = condition.split(":");
        return values[0] <= termValue && values[1] >= termValue;
    }

    // deal with x
    if (!isNaN(parseInt(condition))) {
        return parseInt(condition) === termValue;
    }

    // deal with text
    if (termValue == null) {
        return false;
    }
    return condition === termValue.toString();
}

/**
 * Returns a functionally identical version selector that has all whitespace and non-necessary characters removed, and which is in lower case (because upper case is illegal)
 *
 * If the input is an illegal version selector, then it is possible that this will return something that is functionally different. You should check for legality first.
 * @param versionSelector
 */
export function sanitizeVersionSelector(versionSelector: string): string {
    return versionSelector.replace(/[^0-9a-zA-Z\-+:().[\],*!]*/g, "").toLowerCase();
}
